// components/Admin/ImageUploaderCFA.jsx
import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { Upload, Loader, X } from 'lucide-react'
import { supabase } from '../../lib/supabaseClient'

const ImageUploaderCFA = ({ onImageSelect, label = "Image", currentImage = null }) => {
  const [uploading, setUploading] = useState(false)
  const [preview, setPreview] = useState(currentImage)
  const [error, setError] = useState(null)

  const uploadImage = async (event) => {
    const file = event.target.files?.[0]
    if (!file) return

    // Vérifier le type de fichier
    if (!file.type.startsWith('image/')) {
      setError('Veuillez sélectionner une image valide')
      return
    }

    // Vérifier la taille (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError("L'image ne doit pas dépasser 5MB")
      return
    }

    setUploading(true)
    setError(null)

    try {
      // Générer un nom de fichier unique
      const fileExt = file.name.split('.').pop()
      const fileName = `${Math.random().toString(36).substring(2)}-${Date.now()}.${fileExt}`
      const filePath = `actualites-cfa/${fileName}`

      // Upload vers Supabase Storage
      const { data, error: uploadError } = await supabase.storage
        .from('images')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        })

      if (uploadError) throw uploadError

      // Récupérer l'URL publique
      const { data: { publicUrl } } = supabase.storage
        .from('images')
        .getPublicUrl(filePath)

      setPreview(publicUrl)
      onImageSelect(publicUrl)
    } catch (error) {
      console.error('Erreur upload:', error)
      setError("Erreur lors de l'upload de l'image")
    } finally {
      setUploading(false)
    }
  }

  const removeImage = () => {
    setPreview(null)
    onImageSelect('')
    setError(null)
  }

  return (
    <div>
      <label className="block text-sm font-medium text-gray-300 mb-3">{label}</label>
      
      {!preview ? (
        <div>
          <label
            htmlFor="image-upload-cfa"
            className={`relative block w-full border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all ${
              uploading
                ? 'border-green-500 bg-green-500/10'
                : 'border-slate-600 hover:border-green-500 hover:bg-slate-800/50'
            }`}
          >
            <input
              id="image-upload-cfa"
              type="file"
              accept="image/*"
              onChange={uploadImage}
              disabled={uploading}
              className="hidden"
            />
            
            {uploading ? (
              <div className="flex flex-col items-center gap-3">
                <Loader className="w-12 h-12 text-green-500 animate-spin" />
                <p className="text-sm text-gray-400">Upload en cours...</p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3">
                <Upload className="w-12 h-12 text-gray-400" />
                <div>
                  <p className="text-sm font-medium text-white mb-1">
                    Cliquez pour uploader une image
                  </p>
                  <p className="text-xs text-gray-400">
                    PNG, JPG, WEBP jusqu'à 5MB
                  </p>
                </div>
              </div>
            )}
          </label>

          {error && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-sm text-red-400 mt-2 flex items-center gap-2"
            >
              <X className="w-4 h-4" />
              {error}
            </motion.p>
          )}
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="relative group"
        >
          <img
            src={preview}
            alt="Preview"
            className="w-full h-64 object-cover rounded-lg border border-slate-600"
          />
          
          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-3">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={removeImage}
              type="button"
              className="p-3 bg-red-600 hover:bg-red-700 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-white" />
            </motion.button>
            
            <label
              htmlFor="image-upload-replace-cfa"
              className="p-3 bg-green-600 hover:bg-green-700 rounded-full transition-colors cursor-pointer"
            >
              <Upload className="w-5 h-5 text-white" />
              <input
                id="image-upload-replace-cfa"
                type="file"
                accept="image/*"
                onChange={uploadImage}
                disabled={uploading}
                className="hidden"
              />
            </label>
          </div>

          {uploading && (
            <div className="absolute inset-0 bg-black/80 rounded-lg flex items-center justify-center">
              <Loader className="w-8 h-8 text-green-500 animate-spin" />
            </div>
          )}
        </motion.div>
      )}
    </div>
  )
}

export default ImageUploaderCFA